'use client';

import { useContext } from 'react';
import MarketingNav from '@/app/marketing/components/MarketingNav';
import MarketingFooter from '@/app/marketing/components/MarketingFooter';
import { changelog, changelogCopy, type ChangeKind, type LocalisedText } from '@/data/changelog';
import { LangCurrencyContext, type LangCurrencyContextType } from '@/contexts/LangCurrencyContext';

const KIND_LABEL: Record<ChangeKind, LocalisedText> = {
  added: { en: 'Added', th: 'เพิ่ม' },
  changed: { en: 'Changed', th: 'เปลี่ยนแปลง' },
  fixed: { en: 'Fixed', th: 'แก้ไข' },
  removed: { en: 'Removed', th: 'นำออก' },
  security: { en: 'Security', th: 'ความปลอดภัย' },
};

const KIND_STYLE: Record<ChangeKind, string> = {
  added: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
  changed: 'bg-sky-500/15 text-sky-300 border-sky-500/30',
  fixed: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
  removed: 'bg-rose-500/15 text-rose-300 border-rose-500/30',
  security: 'bg-violet-500/15 text-violet-300 border-violet-500/30',
};

/**
 * Release notes are data, not page chrome, so they are resolved from the
 * active locale in the component rather than left to the DOM-level
 * AutoTranslate fallback. Both the entries and this page's copy come from
 * src/data/changelog so the unit test can enforce Thai coverage on both.
 */

function formatDate(iso: string, lang: 'EN' | 'TH'): string {
  return new Date(iso).toLocaleDateString(lang === 'TH' ? 'th-TH' : 'en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

export default function ChangelogPage() {
  const ctx = useContext(LangCurrencyContext as React.Context<LangCurrencyContextType | null>);
  const lang = ctx?.lang === 'TH' ? 'TH' : 'EN';
  const t = (s: LocalisedText) => (lang === 'TH' ? s.th : s.en);
  const hero = changelogCopy[lang];

  // Latest release as versioned structured data, so search engines can show
  // "new version available" context alongside the page.
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: 'Glanz CRM Changelog',
    url: 'https://glanzcrm.com/changelog',
    isPartOf: { '@type': 'WebSite', name: 'Glanz CRM', url: 'https://glanzcrm.com' },
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: changelog.map((entry, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        item: {
          '@type': 'SoftwareApplication',
          name: 'Glanz CRM',
          softwareVersion: entry.version,
          releaseDate: entry.date,
          description: entry.summary.en,
          applicationCategory: 'BusinessApplication',
        },
      })),
    },
  };

  return (
    <div className="min-h-screen bg-[#0a0e1a] text-[#e5e7eb]">
      <MarketingNav />
      <main id="main-content" className="pt-28 pb-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
          />

          <header className="mb-12">
            <h1 className="text-4xl font-bold text-white mb-3 tracking-tight">{hero.title}</h1>
            <p className="text-[#9ca3af] leading-relaxed">{hero.blurb}</p>
            <a
              href="/changelog/rss.xml"
              className="inline-flex items-center gap-2 mt-5 text-sm text-violet-400 underline"
            >
              {hero.rss}
            </a>
          </header>

          <div className="space-y-12">
            {changelog.map((entry) => (
              <article
                key={entry.version}
                id={entry.version}
                className="rounded-2xl border border-white/10 bg-[#0f1524] p-6 sm:p-8"
              >
                <div className="flex flex-wrap items-center gap-3 mb-2">
                  <h2 className="text-2xl font-bold text-white">{t(entry.title)}</h2>
                  <span className="px-2.5 py-1 rounded-full text-xs font-mono bg-violet-500/15 text-violet-300 border border-violet-500/30">
                    v{entry.version}
                  </span>
                  {entry.breaking && (
                    <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-rose-500/15 text-rose-300 border border-rose-500/30">
                      {hero.breaking}
                    </span>
                  )}
                </div>
                <time dateTime={entry.date} className="block text-xs text-[#9ca3af] mb-4 font-mono">
                  {formatDate(entry.date, lang)}
                </time>
                <p className="text-sm text-[#9ca3af] leading-relaxed mb-5">{t(entry.summary)}</p>
                <ul className="space-y-2.5">
                  {entry.changes.map((change) => (
                    <li key={change.kind + change.text.en} className="flex gap-3 items-start">
                      <span
                        className={`shrink-0 mt-0.5 px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide border ${KIND_STYLE[change.kind]}`}
                      >
                        {t(KIND_LABEL[change.kind])}
                      </span>
                      <span className="text-sm text-[#cbd5e1] leading-relaxed">
                        {t(change.text)}
                      </span>
                    </li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
        </div>
      </main>
      <MarketingFooter />
    </div>
  );
}
